import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, Play, Loader2, RotateCcw, ArrowUpRight, Coffee, Check, X } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, DOMAIN_SHORT } from "@/lib/api";
import { loadMock, saveMock, clearMock, createMock, nextStation, overall, safeVotes } from "@/lib/mock";

const ratingColor = (r) => ({ "Clear pass": "var(--ok)", Pass: "var(--ok)", Borderline: "var(--warn)" }[r] || "var(--alert)");
const COUNTS = [[8, "Full exam day · 8 stations"], [4, "Half day · 4 stations"]];
const BREAK_AFTER = 4;

const Setup = ({ onStart, busy }) => {
  const [n, setN] = useState(8);
  return (
    <section data-testid="mock-setup" className="card p-6 sm:p-8 mt-8 fade-up">
      <h2 className="font-display text-base md:text-lg font-semibold">How many stations?</h2>
      <p className="mt-1 text-sm" style={{ color: "var(--ink-2)" }}>Each station is 15 minutes (3 min reading, 12 min viva), drawn from different domains. Like the real SSSA, the full day is 8 stations with a break after station 4. Your answers are marked in the background while you carry on, then you get one overall result.</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {COUNTS.map(([c, label]) => (
          <button key={c} data-testid={`mock-count-${c}`} onClick={() => setN(c)} className={`h-11 px-5 rounded-full text-sm font-semibold ${n === c ? "btn-primary" : "btn-ghost"}`}>
            {label}
          </button>
        ))}
      </div>
      <button data-testid="mock-start-button" onClick={() => onStart(n)} disabled={busy} className="btn-primary mt-6 h-12 px-7 rounded-full font-semibold flex items-center gap-2">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Start mock exam
      </button>
    </section>
  );
};

const StationList = ({ m, onBegin, next }) => (
  <section data-testid="mock-progress" className="card p-6 sm:p-8 mt-8 fade-up">
    <h2 className="font-display text-base md:text-lg font-semibold">Station {next + 1} of {m.stations.length}</h2>
    {next === BREAK_AFTER && m.stations.length > BREAK_AFTER ? (
      <div data-testid="mock-break-notice" className="mt-3 rounded-xl p-4 text-sm flex gap-3" style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)" }}>
        <Coffee size={18} className="shrink-0" style={{ color: "var(--accent)" }} />
        <p><strong>Break time.</strong> You're halfway through. Step away from the screen, have a drink and something to eat, then come back for stations 5–8 when you're ready.</p>
      </div>
    ) : next > 0 && <p className="mt-1 text-sm flex items-center gap-2" style={{ color: "var(--ink-2)" }}><Coffee size={15} /> Take a short breather, then start when ready. The case is revealed when reading time begins.</p>}
    <ol className="mt-5 space-y-2">
      {m.stations.map((s, i) => (
        <li key={s.id} data-testid={`mock-station-${i}`} className={`flex items-center gap-3 text-sm rounded-lg px-3 py-2 ${i === BREAK_AFTER ? "mt-4 border-t pt-4 rounded-none" : ""}`} style={{ background: i === next ? "var(--muted-bg)" : "transparent", borderColor: "var(--line)" }}>
          <span className="font-mono text-xs w-16 shrink-0" style={{ color: "var(--ink-3)" }}>Station {i + 1}</span>
          <span className="flex-1">{m.jobs[i] ? s.title : `D${s.domain} · ${DOMAIN_SHORT[s.domain - 1]}`}</span>
          <span className="text-xs font-semibold" style={{ color: m.jobs[i] ? "var(--ok)" : "var(--ink-3)" }}>{m.jobs[i] ? "Done" : i === next ? "Next" : "To come"}</span>
        </li>
      ))}
    </ol>
    <button data-testid="mock-begin-station-button" onClick={onBegin} className="btn-primary mt-6 h-12 px-7 rounded-full font-semibold flex items-center gap-2"><Play size={16} /> Begin station {next + 1}</button>
  </section>
);

const Overall = ({ m }) => {
  const done = Object.keys(m.results).length === m.stations.length;
  const o = done ? overall(m.results, m.stations.length) : null;
  return (
    <section data-testid="mock-results" className="mt-8 fade-up">
      {!done ? (
        <div data-testid="mock-marking" className="card p-6 flex items-center gap-3 text-sm"><Loader2 size={18} className="animate-spin" /> The examiners are marking your stations ({Object.keys(m.results).length}/{m.stations.length})…</div>
      ) : (
        <div className="card p-6">
          <div className="grid sm:grid-cols-[auto,1fr] gap-6 items-center">
            <div className="text-center sm:pr-6 sm:border-r" style={{ borderColor: "var(--line)" }}>
              <div data-testid="mock-overall-score" className="font-mono text-5xl font-bold">{o.avg}<span className="text-2xl">%</span></div>
              <div data-testid="mock-overall-rating" className="mt-2 inline-block px-3 py-1 rounded-full text-sm font-semibold text-white" style={{ background: o.pass ? "var(--ok)" : "var(--alert)" }}>{o.pass ? "Overall pass" : "Overall fail"}</div>
            </div>
            <ul data-testid="mock-pass-rules" className="space-y-2 text-sm">
              {o.rules.map((r) => (
                <li key={r.key} data-testid={`mock-rule-${r.key}`} className="flex items-start gap-2">
                  {r.ok ? <Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--ok)" }} /> : <X size={16} className="mt-0.5 shrink-0" style={{ color: "var(--alert)" }} />}
                  <span className="flex-1" style={{ color: "var(--ink-2)" }}>{r.label}</span>
                  <span className="font-mono font-bold shrink-0" style={{ color: r.ok ? "var(--ok)" : "var(--alert)" }}>{r.value}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-4 text-xs" style={{ color: "var(--ink-3)" }}>You must meet all three, as in the ANZCA exam reports{m.stations.length !== 8 ? " (thresholds scaled for a half day)" : ""}. Each station's two examiner safety judgements are made independently by the AI marker. Practice estimate only.</p>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {m.stations.map((s, i) => {
          const r = m.results[i];
          return (
            <div key={s.id} data-testid={`mock-result-${i}`} className="card p-4 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono text-xs w-16" style={{ color: "var(--ink-3)" }}>Station {i + 1}</span>
              <span className="flex-1 min-w-[10rem] font-medium">{s.title}</span>
              {r ? <>
                <span className="font-mono font-bold" style={{ color: r.overall_score >= 50 ? "var(--ok)" : "var(--alert)" }}>{r.overall_score}%</span>
                <span data-testid={`mock-result-safe-${i}`} className="font-mono text-xs px-2 py-0.5 rounded-md" style={{ background: "var(--muted-bg)" }}>Safe {safeVotes(r)}/2</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: ratingColor(r.global_rating) }}>{r.global_rating}</span>
                <Link data-testid={`mock-result-link-${i}`} to={`/results/${r.id}`} className="btn-ghost h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1">Feedback <ArrowUpRight size={13} /></Link>
              </> : r === null ? <span style={{ color: "var(--alert)" }}>Marking failed</span> : <Loader2 size={15} className="animate-spin" />}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const usePoll = (m, setM) => {
  useEffect(() => {
    if (!m || nextStation(m) !== -1) return;
    const pending = Object.entries(m.jobs).filter(([i]) => m.results[i] === undefined);
    if (!pending.length) return;
    const t = setTimeout(async () => {
      const upd = { ...m, results: { ...m.results } };
      await Promise.all(pending.map(async ([i, job]) => {
        try {
          const { data } = await http.get(`/jobs/${job}`);
          if (data.status === "done") upd.results[i] = { id: data.result.id, overall_score: data.result.overall_score, global_rating: data.result.global_rating, examiners: data.result.examiners || [] };
          if (data.status === "error") upd.results[i] = null;
        } catch (e) { if (e.response?.status === 404) upd.results[i] = null; }
      }));
      saveMock(upd);
      setM(upd);
    }, 2500);
    return () => clearTimeout(t);
  }, [m, setM]);
};

const useSaveResult = (m, setM) => {
  const sending = useRef(false);
  useEffect(() => {
    if (!m || m.saved || sending.current || nextStation(m) !== -1 || Object.keys(m.results).length !== m.stations.length) return;
    const o = overall(m.results, m.stations.length);
    const stations = m.stations.map((s, i) => m.results[i] && { title: s.title, score: m.results[i].overall_score, safe: safeVotes(m.results[i]) }).filter(Boolean);
    if (!stations.length) return;
    sending.current = true;
    http.post("/mock-results", { mock_id: m.id, stations, avg: o.avg, passed: o.pass, rules: o.rules.map(({ key, label, value, ok }) => ({ key, label, value, ok })) })
      .then(() => { const upd = { ...m, saved: true }; saveMock(upd); setM(upd); })
      .catch(() => {})
      .finally(() => { sending.current = false; });
  }, [m, setM]);
};

export default function MockExam() {
  const nav = useNavigate();
  const [m, setM] = useState(loadMock);
  const [busy, setBusy] = useState(false);
  usePoll(m, setM);
  const start = (n) => { setBusy(true); createMock(n).then(setM).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false)); };
  const reset = () => { clearMock(); setM(null); };
  const next = m ? nextStation(m) : -1;
  useSaveResult(m, setM);
  return (
    <div data-testid="mock-exam-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 pb-24">
      <p className="font-mono text-xs uppercase tracking-widest flex items-center gap-2" style={{ color: "var(--accent)" }}><CalendarClock size={14} /> Mock Exam Day</p>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Back-to-back stations, one overall result</h1>
      {!m && <Setup onStart={start} busy={busy} />}
      {m && next >= 0 && <StationList m={m} next={next} onBegin={() => nav(`/viva/${m.stations[next].id}?mock=1`)} />}
      {m && next === -1 && <Overall m={m} />}
      {m && (
        <button data-testid="mock-reset-button" onClick={reset} className="btn-ghost mt-6 h-10 px-5 rounded-full text-sm font-medium flex items-center gap-2">
          <RotateCcw size={14} /> {next === -1 ? "Start a new mock exam" : "Abandon and start over"}
        </button>
      )}
    </div>
  );
}
